import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertFeed = () => {
  const [filter, setFilter] = useState('all');

  const alerts = [
    {
      id: 1,
      severity: 'critical',
      title: 'Payment Gateway Timeout',
      description: 'High response times detected on payment processing service',
      affectedSystems: ['Payment Service', 'Order Processing'],
      timestamp: new Date(Date.now() - 300000),
      status: 'active',
      assignee: 'DevOps Team'
    },
    {
      id: 2,
      severity: 'warning',
      title: 'High CPU Usage',
      description: 'Server cluster showing 85% CPU utilization',
      affectedSystems: ['Web Servers', 'API Gateway'],
      timestamp: new Date(Date.now() - 900000),
      status: 'investigating',
      assignee: 'Infrastructure Team'
    },
    {
      id: 3,
      severity: 'info',
      title: 'Scheduled Maintenance',
      description: 'Database maintenance window starting in 2 hours',
      affectedSystems: ['Database Cluster'],
      timestamp: new Date(Date.now() - 1800000),
      status: 'scheduled',
      assignee: 'Database Team'
    },
    {
      id: 4,
      severity: 'warning',
      title: 'Unusual Traffic Pattern',
      description: 'Traffic spike detected in food delivery vertical',
      affectedSystems: ['Load Balancer', 'CDN'],
      timestamp: new Date(Date.now() - 2700000),
      status: 'resolved',
      assignee: 'Monitoring Team'
    },
    {
      id: 5,
      severity: 'critical',
      title: 'Service Degradation',
      description: 'Partner verification service experiencing timeouts',
      affectedSystems: ['KYC Service', 'Partner Portal'],
      timestamp: new Date(Date.now() - 3600000),
      status: 'active',
      assignee: 'Backend Team'
    }
  ];

  const severityFilters = [
    { value: 'all', label: 'All Alerts', count: alerts?.length },
    { value: 'critical', label: 'Critical', count: alerts?.filter(a => a?.severity === 'critical')?.length },
    { value: 'warning', label: 'Warning', count: alerts?.filter(a => a?.severity === 'warning')?.length },
    { value: 'info', label: 'Info', count: alerts?.filter(a => a?.severity === 'info')?.length }
  ];

  const filteredAlerts = filter === 'all' ? alerts : alerts?.filter(alert => alert?.severity === filter);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error border-error bg-error/10';
      case 'warning': return 'text-warning border-warning bg-warning/10';
      case 'info': return 'text-primary border-primary bg-primary/10';
      default: return 'text-muted-foreground border-border bg-muted/50';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-error';
      case 'investigating': return 'text-warning';
      case 'resolved': return 'text-success';
      case 'scheduled': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp?.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ago`;
    return `${minutes}m ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Alert Feed</h3>
          <p className="text-sm text-muted-foreground">Real-time system alerts and incidents</p>
        </div>
        <Button variant="ghost" size="sm" iconName="Settings">
          Configure
        </Button>
      </div>
      {/* Severity Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {severityFilters?.map((severityFilter) => (
          <button
            key={severityFilter?.value}
            onClick={() => setFilter(severityFilter?.value)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-hover ${
              filter === severityFilter?.value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
            }`}
          >
            <span>{severityFilter?.label}</span>
            <span className="bg-background/20 px-1.5 py-0.5 rounded text-xs">
              {severityFilter?.count}
            </span>
          </button>
        ))}
      </div>
      {/* Alert List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredAlerts?.map((alert) => (
          <div
            key={alert?.id}
            className={`border rounded-lg p-4 transition-hover hover:elevation-1 ${getSeverityColor(alert?.severity)}`}
          >
            {/* Alert Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3">
                <Icon 
                  name={getSeverityIcon(alert?.severity)} 
                  size={20} 
                  className={getSeverityColor(alert?.severity)?.split(' ')?.[0]}
                />
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{alert?.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{alert?.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium ${getStatusColor(alert?.status)}`}>
                  {alert?.status?.toUpperCase()}
                </span>
                <Button variant="ghost" size="sm" iconName="ExternalLink">
                  Investigate
                </Button>
              </div>
            </div>

            {/* Affected Systems */}
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-1">Affected Systems:</p>
              <div className="flex flex-wrap gap-1">
                {alert?.affectedSystems?.map((system, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-muted text-xs font-medium rounded"
                  >
                    {system}
                  </span>
                ))}
              </div>
            </div>

            {/* Alert Footer */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center space-x-4">
                <span>{formatTimeAgo(alert?.timestamp)}</span>
                <span>Assigned to: {alert?.assignee}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" iconName="MessageSquare">
                  Comment
                </Button>
                <Button variant="ghost" size="sm" iconName="UserPlus">
                  Assign
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Plus">
              Create Alert
            </Button>
            <Button variant="ghost" size="sm" iconName="Filter">
              Advanced Filter
            </Button>
          </div>
          <Button variant="ghost" size="sm" iconName="Download">
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlertFeed;